// store/features/crm.feature.tsx
import {
  CreateSyncProfileRequest,
  CrmAdminPanelCrm,
  CrmLookupConfig,
  CrmMatchKey,
  CrmOption,
  CrmSettings,
  CrmSettingsRequest,
  CrmUsageResponse,
  CrmUserMappingsResponse,
  CrmUserRouteCrm,
  CrmUsersResponse,
  EnrichmentType,
  GetSyncLogParams,
  PutCrmUserMappingsRequest,
  SyncLogResponse,
  SyncProfile,
  SyncProfileListResponse,
  UpdateSyncProfileRequest,
} from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';
import { leadListsApi } from './lead-lists.feature';

export interface RemoveCrmConnectionResponse {
  connection_type: string;
  status: string;
}

export interface MigrateLeadsToCrmRequest {
  lead_ids: string[];
  connection_type: string;
  lead_list_id?: string;
  field_mapping?: FieldMappingItem[];
  object_type?: string;
  campaign_id?: string;
  operation?: 'create' | 'update' | 'upsert' | 'lookup' | 'add_to_sequence';
  match_key?: string;
  /** 'refresh' overwrites stale CRM values on an idempotent re-sync. */
  preset?: string;
  /**
   * CLE-423: manual single-record sync lifts the owned-record/open-deal
   * guards. Sent only by the explicit per-row icon/popover click; the backend
   * additionally requires exactly one lead and the org's
   * manual_sync_guard_override_v1 flag (fail closed), so this field alone
   * grants nothing.
   */
  override_record_guards?: boolean;
}

export interface HubSpotPropertyOption {
  label?: string;
  value?: string;
}

export interface HubSpotProperty {
  name: string;
  label: string;
  type: string;
  group_name?: string;
  field_type?: string;
  /** Salesforce describe capability. Undefined for HubSpot/legacy caches. */
  filterable?: boolean;
  /** Exact Salesforce write capabilities. Undefined for other providers. */
  createable?: boolean;
  updateable?: boolean;
  read_only: boolean;
  hidden: boolean;
  options: HubSpotPropertyOption[];
  /** Operators a ``field:`` exclusion can use on this property. Sent only
   *  with ``purpose=exclusion``; the list follows the property's type. */
  exclusion_ops?: string[];
  /** Whether the field holds a CRM user: a Salesforce User lookup or a
   *  HubSpot owner property. Absent when the API can't tell (an older API
   *  or cache). */
  holds_user?: boolean;
}

export interface HubSpotPropertiesResponse {
  properties: HubSpotProperty[];
  last_refreshed?: string;
}

export type { CrmUserRouteCrm };

/** Attio object slugs the play pickers read. */
export type AttioObjectType = 'people' | 'companies' | 'deals';

/** What `GET /crm/attio/attributes` returns rows for. `lookup` lists every
 *  attribute a Look up can read (select options by id). `update` and `sync`
 *  list what a write can set (options by title). `exclusion` lists only what
 *  a `field:` exclude clause can name, each row with its `exclusion_ops`. */
export type AttioAttributePurpose = 'lookup' | 'update' | 'sync' | 'exclusion';

/** Sync and exclusion run on people and companies only; the API refuses
 *  them on deals, so the type does too. */
export type GetAttioAttributesArgs = {
  organizationId: string;
  workspaceId?: string;
} & (
  | { objectType: 'people' | 'companies'; purpose: AttioAttributePurpose }
  | { objectType: 'deals'; purpose: 'lookup' | 'update' }
);

/** One Attio list from `GET /crm/attio/lists`. `object` is the parent as
 *  Cleanlist's word (Contact, Account, Deal); a custom object keeps its slug. */
export interface AttioList {
  id: string;
  name: string;
  slug: string;
  object: string;
}

export type CrmWriteMode =
  | 'never'
  | 'fill_if_empty'
  | 'overwrite_if_different'
  | 'always_overwrite';

export interface FieldMappingItem {
  source_column: string;
  hubspot_property: string;
  source_label?: string;
  hubspot_label?: string;
  // Per-field overwrite condition for the write-safety merge engine. Undefined
  // means "inherit the safe default" (fill_if_empty) on the backend.
  write_mode?: CrmWriteMode;
  // Explicit per-property opt-out. When true the push path skips this row
  // entirely (no create, no update). Distinct from an unconfigured row (no
  // hubspot_property, do_not_sync falsy): this is a persistent "off" the user
  // chose via the "Don't sync to {CRM}" dropdown option. The CRM-property field
  // is blanked when this is set.
  do_not_sync?: boolean;
}

export interface FieldMappingTemplate {
  id: string;
  name: string;
  description?: string;
  crm_type: string;
  object_type: string;
  mappings: FieldMappingItem[];
  is_default: boolean;
  times_used: number;
}

export interface FieldMappingTemplateListResponse {
  templates: FieldMappingTemplate[];
  total: number;
  default_template_id?: string;
}

export interface BackfillListToCrmRequest {
  connection_type: string;
  lead_list_id: string;
  object_type?: string;
  field_mapping?: FieldMappingItem[];
  preset?: string;
}

export type CrmPushOutcome = 'done' | 'skipped' | 'failed';

export interface LeadMigrationResult {
  lead_id: string;
  success: boolean;
  crm_lead_id?: string;
  error?: string;
  // Three-way outcome {done,skipped,failed}; reason carries the benign skip
  // reason code (no_writable_fields, no_account, ...). Optional for back-compat
  // with older BE responses.
  outcome?: CrmPushOutcome | null;
  reason?: string;
}

export interface LeadMigrationResponse {
  total_leads: number;
  successful_migrations: number;
  // Real failures only — benign skips are reported in skipped_migrations.
  failed_migrations: number;
  // Optional + defaulted for back-compat with older BE responses that predate
  // the three-way outcome (treat missing as 0).
  skipped_migrations?: number;
  results: LeadMigrationResult[];
}

export interface MartechList {
  id: string;
  name: string;
  description?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  /** Native list metadata; an unknown processing type proves no write target. */
  static?: boolean;
  raw?: Record<string, unknown>;
}

/**
 * Salesforce list view (custom-playbooks inbound trigger, build-v3 §2f L1).
 * Wire: GET /crm/salesforce/list-views?object_type=Contact|Lead — gated by
 * require_custom_playbooks on the BE (flag-404 + AppSumo-403, CR-13).
 */
export interface SalesforceListView {
  id: string;
  name: string;
  /** The sObject the view belongs to (Contact | Lead). */
  sobject: string;
}

/**
 * Salesforce report (custom-playbooks inbound trigger, build-v3 §2f L2).
 * Wire: GET /crm/salesforce/reports — SOQL `SELECT Id, Name, Format,
 * FolderName FROM Report` on the BE; same require_custom_playbooks gate.
 * Only the first 2,000 rows of a report are watched (Analytics REST cap) —
 * the picker surfaces that as helper text, not a field here.
 */
export interface SalesforceReport {
  id: string;
  name: string;
  format?: string;
  folder?: string;
}

export interface MartechMember {
  id: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  // API returns either plain strings or objects with email property
  emails?: (string | { email: string })[];
  phones?: { phone_number: string }[];
  phone?: string;
  mobile_phone?: string;
  linkedin_url?: string;
  public_linkedin_url?: string;
  jobtitle?: string;
  tags?: string[];
  company?: string;
  custom_fields?: Record<string, unknown>;
}

export interface CrmContactData {
  crm_contact_id?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  email?: string;
  phone?: string;
  mobile_phone?: string;
  linkedin_url?: string;
  custom_fields?: Record<string, unknown>;
}

export interface ImportCrmListRequest {
  connection_type: string;
  contacts: CrmContactData[];
  lead_list_name: string;
  lead_list_id?: string;
  enrichment_type: EnrichmentType;
  crm_list_id?: string;
  enable_sync?: boolean;
  object_type?: string;
}

export interface ImportCrmListResponse {
  task_id: string;
  message: string;
  total_contacts: number;
  lead_list_id: string;
}

export interface ToggleCrmPollResponse {
  crm_poll_enabled: boolean;
}

export interface TriggerCrmPollResponse {
  task_id: string;
  message: string;
}

export interface LookupHubSpotContactsRequest {
  organizationId: string;
  lead_list_id: string;
  lead_ids?: string[];
  config: CrmLookupConfig;
}

// ---------------------------------------------------------------------------
// Per-CRM sync-status column (Salesforce + HubSpot)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// CRM-based exclusion sets (FE-E — People-search CRM exclusion filters)
// ---------------------------------------------------------------------------
//
// Resolve a CRM condition as inline identifiers or, for an indexed-capable
// client, a pinned server-side generation reference. People Search carries an
// indexed reference through preview, paging, and import and the API evaluates
// it against candidate identities. Backward-compatible backend contract:
//   POST /crm/exclusion-set
//     body { crm, condition, force_refresh?, supports_indexed? }
//     200  { crm, condition, identifiers, fetched_at, stale, truncated,
//            partial, record_count, reason, mode, reference }
//          mode='inline' has reference=null; mode='indexed' has a pinned
//          { crm, condition, generation_id } reference.
//   400  unsupported_crm | unknown_condition | invalid_owner_id |
//        owner_unresolved | crm_not_connected

/** CRMs that support exclusion-set resolution. Kept local to this module
 *  (rather than importing ``CrmSyncCrmType`` from use-crm-sync-status,
 *  which itself imports this file) so there's no import cycle. The two are
 *  structurally identical; ``ConnectedSyncCrm['type']`` assigns cleanly. */
export type CrmExclusionCrm = 'salesforce' | 'hubspot';

/** Explicit object scope accepted by last-activity exclusion conditions. */
export type CrmLastActivityScope = 'accounts' | 'contacts' | 'both';

/** Account-status conditions support both the legacy default-field form and
 *  an explicitly selected CRM field. The second member is documented
 *  separately even though it is structurally covered by the first. */
export type CrmFieldQualifiedAccountStatusCondition =
  `account_status:@${string}:${string}`;
export type CrmAccountStatusCondition =
  | `account_status:${string}`
  | CrmFieldQualifiedAccountStatusCondition;

/** Last-activity conditions accept a positive integer day count at runtime.
 *  Scope may be omitted (the backend default is ``both``) or explicit. */
export type CrmLastActivityCondition =
  | `last_activity_over:${number}`
  | `last_activity_over:${number}:${CrmLastActivityScope}`
  | `last_activity_within:${number}`
  | `last_activity_within:${number}:${CrmLastActivityScope}`;

/** The CRM record a ``field:`` condition reads. HubSpot has no leads; the
 *  server reads ``company`` as ``account``. */
export type CrmFieldObject = 'contact' | 'lead' | 'account';
/** Operators of a ``field:`` condition. ``empty`` and ``not_empty`` take no
 *  value; the rest carry everything after the third colon as the value. */
export type CrmFieldOp =
  | 'eq'
  | 'ne'
  | 'contains'
  | 'gt'
  | 'lt'
  | 'empty'
  | 'not_empty';
/** ``field:<object>.<Field>:<op>[:<value>]``. The value may itself contain
 *  colons. */
export type CrmFieldCondition =
  | `field:${CrmFieldObject}.${string}:${CrmFieldOp}`
  | `field:${CrmFieldObject}.${string}:${CrmFieldOp}:${string}`;

/** Conditions accepted by POST /crm/exclusion-set. Runtime validation remains
 *  authoritative for non-empty IDs/values and positive integer day counts. */
export type CrmExclusionCondition =
  | 'current_customers'
  | 'active_opportunities'
  | 'owned_by_me'
  | 'owned_by_others'
  | 'in_crm'
  | 'owned_any'
  | 'crm_accounts'
  | `owner:${string}`
  | CrmAccountStatusCondition
  | CrmLastActivityCondition
  | CrmFieldCondition
  | `owned_active_within:${number}`;

/** Safe reason codes returned in a successful exclusion-set response. Search
 *  safety is decided from ``partial``, ``stale`` and ``truncated``; this value
 *  records why the response is incomplete or why a refresh was not performed. */
export type CrmExclusionReason =
  | 'budget'
  | 'refresh_throttled'
  | 'owner_unresolved'
  | 'rate_limited'
  | 'hubspot_no_deals'
  | 'call_cap'
  | 'domain_unavailable'
  | 'linkedin_unavailable'
  | 'partial_response'
  | 'refresh_failed';

export interface CrmExclusionIdentifiers {
  domains: string[];
  linkedin_urls: string[];
  names: string[];
}

export interface CrmExclusionSetRequest {
  organizationId: string;
  crm: CrmExclusionCrm;
  condition: CrmExclusionCondition;
  /** Bypass the server-side freshness cache. Wired to the "refresh"
   *  affordance in the sidebar. */
  force_refresh?: boolean;
  /** Advertises that this client can carry the server-side generation
   * reference through every preview, paging and import request. */
  supports_indexed?: boolean;
}

export interface CrmExclusionReference {
  crm: CrmExclusionCrm;
  condition: CrmExclusionCondition;
  generation_id: string;
}

interface CrmExclusionSetResponseBase {
  crm: CrmExclusionCrm;
  condition: CrmExclusionCondition;
  identifiers: CrmExclusionIdentifiers;
  /** ISO timestamp of when this set was computed, or null when never
   *  computed (first fetch in flight / empty). */
  fetched_at: string | null;
  /** Cache is past its freshness window — values may be out of date. */
  stale: boolean;
  /** The identifier set was capped server-side; some matches are missing. */
  truncated: boolean;
  /** A graceful degradation occurred (see ``reason``); the set is best
   *  effort. Always HTTP 200. */
  partial: boolean;
  record_count: number;
  reason: CrmExclusionReason | null;
}

export type CrmExclusionSetResponse = CrmExclusionSetResponseBase &
  (
    | { mode: 'inline'; reference: null }
    | { mode: 'indexed'; reference: CrmExclusionReference }
  );

// ---------------------------------------------------------------------------
// CRM status picklist options (CP-4b) — drives the account-status MULTI-select
// in each per-CRM filter section (CP-7).
// ---------------------------------------------------------------------------
//
// GET /crm/management/{crm_type}/status-options ->
//   { options: [{ field, label, options: [{ value, label }] }] }
// Each top-level group is one CRM picklist field (e.g. Salesforce
// Account.Type, HubSpot lifecyclestage); ``field`` is the API name, ``label``
// the human header; the nested ``options`` are the picklist's real values.
// The FE renders these grouped by field and emits one ``account_status:<value>``
// EXCLUDE clause per selected value.

/** One picklist value within a status-options group. */
export interface CrmStatusOption {
  value: string;
  label: string;
}

/** One CRM picklist field and its values. */
export interface CrmStatusOptionGroup {
  field: string;
  label: string;
  options: CrmStatusOption[];
}

export interface CrmStatusOptionsResponse {
  options: CrmStatusOptionGroup[];
}

export type CrmSyncState = 'synced' | 'failed' | 'pending' | 'not_synced';

export interface CrmSyncStatusEntry {
  state: CrmSyncState;
  crm_record_id: string | null;
  /** ISO timestamp of the last sync attempt for this record, if any. */
  last_at: string | null;
  exists_in_crm: boolean | null;
  /** The sync profile that created this person in the CRM, so other syncs only
   * fill their empty fields. Present only when a profile holds the person. */
  held_by?: { id: string; name: string } | null;
}

/** Per-record statuses keyed by CRM type. A CRM key is null when that CRM is
 *  not connected for the org. */
export interface CrmSyncRecordStatuses {
  salesforce: CrmSyncStatusEntry | null;
  hubspot: CrmSyncStatusEntry | null;
  /** Sent only while Attio is connected; left out otherwise. */
  attio?: CrmSyncStatusEntry | null;
  /** Leads only, and only while the org's `pipedrive_v1` flag is on. */
  pipedrive?: CrmSyncStatusEntry | null;
}

export interface CrmSyncStatusResponse {
  /** Keyed by lead UUID (leads endpoint) or lowercased domain (companies). */
  statuses: Record<string, CrmSyncRecordStatuses>;
}

/** BE accepts at most 500 ids/domains per sync-status call. */
const CRM_SYNC_STATUS_BATCH_SIZE = 500;

// ═══════════════════════════════════════════════════════════════════════════
// [FE-D] CRM rich status popover (feat/crm-status-popover)
// Self-contained block: types + `getCrmStatus` endpoint + cache helpers, kept
// together so the later merge with fe-e/fe-f (which also touch this file) is a
// clean splice. The richer per-lead status that backs the hover/click popover
// — distinct from the thin `getCrmSyncStatus` lit/grey column above.
// ═══════════════════════════════════════════════════════════════════════════

// Local literal to avoid an import cycle with use-crm-sync-status.hook.ts
// (which imports the endpoint hooks from this file). Structurally identical to
// the hook's `CrmSyncCrmType`.
export type CrmStatusCrm = 'salesforce' | 'hubspot' | 'attio' | 'pipedrive';
const CRM_STATUS_CRMS: ReadonlySet<string> = new Set<CrmStatusCrm>([
  'salesforce',
  'hubspot',
  'attio',
  'pipedrive',
]);

export type CrmStatusObjectType = 'contact' | 'lead';

// NOTE: every field below mirrors the BE pydantic models in
// cleanse_api/api/crm_status_api.py — CrmOwner / CrmAccount / CrmOpportunity —
// where each field is `Optional[...] = None`. The BE returns nulls for any field
// it cannot resolve (e.g. an owner with no email, an account with no name), so
// the FE types MUST be nullable to match the wire and the popover MUST render
// defensively. Declaring them required risks crashes / null leaking into the UI.
export interface CrmStatusOwner {
  id: string | null;
  name: string | null;
  email: string | null;
  /** True when the record sits in an unassigned owner pool (round-robin /
   *  queue) rather than with a named rep. Drives the "unassigned" treatment
   *  and gates the owner-reassign behaviour of the Update action. null when the
   *  BE could not resolve pool membership (treat conservatively as not-pool). */
  is_pool: boolean | null;
}

export interface CrmStatusAccount {
  id: string | null;
  name: string | null;
  status: string | null;
}

export interface CrmStatusOpportunity {
  id: string | null;
  name: string | null;
  stage: string | null;
  amount: number | null;
}

/** Rich per-lead CRM status — the frozen FE-D backend contract. */
export interface CrmStatus {
  crm: CrmStatusCrm;
  exists_in_crm: boolean;
  object_type: CrmStatusObjectType | null;
  record_id: string | null;
  record_url: string | null;
  matched_by: string | null;
  owner: CrmStatusOwner | null;
  account_owner: CrmStatusOwner | null;
  account: CrmStatusAccount | null;
  has_open_opportunity: boolean;
  open_opportunity: CrmStatusOpportunity | null;
  last_activity_date: string | null;
  lifecycle_status: string | null;
  list_names: string[];
  fetched_at: string;
  stale: boolean;
}

export interface CrmStatusBudget {
  used: number;
  limit: number;
  /** Lead ids the BE could not resolve within budget — render a subtle
   *  "status unavailable, retry" state, NEVER "not in CRM". */
  errored_lead_ids: string[];
}

export interface CrmStatusResponse {
  /** Keyed by lead id. */
  statuses: Record<string, CrmStatus>;
  budget: CrmStatusBudget;
}

interface ConfirmedCrmMigration {
  leadId: string;
  crmRecordId: string;
}

const confirmedCrmMigrations = (
  response: LeadMigrationResponse
): ConfirmedCrmMigration[] =>
  response.results
    .filter(
      (result): result is LeadMigrationResult & { crm_lead_id: string } =>
        result.success &&
        (result.outcome == null || result.outcome === 'done') &&
        Boolean(result.crm_lead_id?.trim())
    )
    .map((result) => ({
      leadId: result.lead_id,
      crmRecordId: result.crm_lead_id.trim(),
    }));

const migrationProofStatus = (
  crm: CrmStatusCrm,
  crmRecordId: string,
  confirmedAt: string,
  previous?: CrmStatus
): CrmStatus => {
  const recordDetails =
    previous?.record_id === crmRecordId ? previous : undefined;
  return {
    crm,
    exists_in_crm: true,
    object_type: recordDetails?.object_type ?? null,
    record_id: crmRecordId,
    record_url: recordDetails?.record_url ?? null,
    matched_by: 'migration',
    owner: recordDetails?.owner ?? null,
    account_owner: recordDetails?.account_owner ?? null,
    account: recordDetails?.account ?? null,
    has_open_opportunity: recordDetails?.has_open_opportunity ?? false,
    open_opportunity: recordDetails?.open_opportunity ?? null,
    last_activity_date: recordDetails?.last_activity_date ?? null,
    lifecycle_status: recordDetails?.lifecycle_status ?? null,
    // Cleanlist list membership belongs to the lead, not the CRM record.
    list_names: previous?.list_names ?? [],
    fetched_at: confirmedAt,
    stale: true,
  };
};

const isConfirmedMigrationProof = (
  status: CrmStatus | undefined
): status is CrmStatus & { record_id: string } =>
  Boolean(
    status?.exists_in_crm &&
    status.matched_by === 'migration' &&
    status.record_id
  );

/**
 * Batch deltas may predate a completed migration. Only a successful forced
 * refresh started against the current proof may replace it with a deletion or
 * a different record. The refresh caller verifies that no newer proof arrived.
 */
const mergeCrmStatusResponse = (
  current: CrmStatusResponse,
  incoming: CrmStatusResponse,
  verifiedLeadId?: string
): void => {
  const incomingErrored = new Set(incoming.budget.errored_lead_ids);
  const resolvedLeadIds = new Set<string>();

  for (const [leadId, status] of Object.entries(incoming.statuses)) {
    const previous = current.statuses[leadId];
    if (
      isConfirmedMigrationProof(previous) &&
      (status.stale ||
        incomingErrored.has(leadId) ||
        !status.exists_in_crm ||
        status.record_id !== previous.record_id) &&
      leadId !== verifiedLeadId
    ) {
      // A rejected detail lookup has not resolved the write proof. Keep its
      // membership and expose Retry even if the server omitted an error ID.
      incomingErrored.add(leadId);
      continue;
    }

    current.statuses[leadId] = status;
    if (!incomingErrored.has(leadId)) resolvedLeadIds.add(leadId);
  }

  const errored = new Set(
    current.budget.errored_lead_ids.filter(
      (leadId) => !resolvedLeadIds.has(leadId)
    )
  );
  for (const leadId of incomingErrored) errored.add(leadId);
  current.budget = {
    used: Math.max(current.budget.used, incoming.budget.used),
    limit: Math.max(current.budget.limit, incoming.budget.limit),
    errored_lead_ids: Array.from(errored),
  };
};

/**
 * Rich-status endpoint is heavier (live owner/account/opportunity reads), so
 * it batches in SMALL chunks per visible page rather than the 500-id sync
 * batch. The endpoint is cache-first server-side; the FE never fires it on
 * hover (data is loaded on list-enter / paginate and served from RTK cache).
 */
const CRM_STATUS_BATCH_SIZE = 50;

// ─────────────────────────────────────────────────────────────────────────────
// Transient-failure auto-retry for the rich-status chunk POSTs (getCrmStatus /
// getCrmStatusByIdentifier — the two queries that feed the CRM status popover).
// Without it a SINGLE dropped connection / 5xx / 429 burst flipped the whole
// batch to `statusUnknown` and every cell rendered the "We couldn't load this
// contact's {CRM} status right now" card. Scoped local loop rather than RTK's
// `retry()` wrapper: the api's baseQuery isn't retry-wrapped, and wrapping it
// (even with maxRetries: 0 + per-endpoint extraOptions) would route EVERY
// endpoint through the retry machinery — this keeps the change to exactly
// these two endpoints. The popover's manual Retry button stays the final
// fallback after the bounded attempts are exhausted.
// ─────────────────────────────────────────────────────────────────────────────

/** 2 retries = 3 attempts; backoff 500ms → 1s (~1.5s worst-case added). */
const CRM_STATUS_MAX_RETRIES = 2;
const CRM_STATUS_RETRY_BASE_DELAY_MS = 500;

/**
 * Retry ONLY transient failures: network drops, timeouts, 429 bursts and 5xx.
 * Every other status (401 — already token-refresh-handled in the baseQuery,
 * 403 — the AppSumo fence, 404, other 4xx) must surface immediately.
 */
const isTransientCrmStatusError = (error: FetchBaseQueryError): boolean => {
  if (error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR') {
    return true;
  }
  return (
    typeof error.status === 'number' &&
    (error.status === 429 || (error.status >= 500 && error.status <= 599))
  );
};

/** The slice of the queryFn `fetchWithBQ` contract the retry loop consumes. */
type CrmStatusChunkResult = {
  data?: unknown;
  error?: FetchBaseQueryError;
};

async function fetchStatusChunkWithRetry(
  fetchWithBQ: (
    args: FetchArgs
  ) => CrmStatusChunkResult | PromiseLike<CrmStatusChunkResult>,
  args: FetchArgs
): Promise<CrmStatusChunkResult> {
  let result = await fetchWithBQ(args);
  for (
    let attempt = 0;
    attempt < CRM_STATUS_MAX_RETRIES &&
    result.error &&
    isTransientCrmStatusError(result.error);
    attempt++
  ) {
    await new Promise((resolve) =>
      setTimeout(resolve, CRM_STATUS_RETRY_BASE_DELAY_MS * 2 ** attempt)
    );
    result = await fetchWithBQ(args);
  }
  return result;
}

// ═══════════════════════════════════════════════════════════════════════════
// [CP-11] CRM rich status BY IDENTIFIER (people search + directory rows that
// are NOT yet Cleanlist leads — no lead_id). Resolves the SAME rich `CrmStatus`
// contract as `getCrmStatus`, but keyed by IDENTIFIER (normalized LinkedIn URL,
// optional secondary email) so search-result / directory rows can show the same
// popover. Wire-matched to BE `POST /crm/status/by-identifier`
// (crm_status_api.py: StatusByIdentifierRequest / StatusByIdentifierResponse).
// ═══════════════════════════════════════════════════════════════════════════

/** One identifier to resolve. `linkedin_url` is the canonical key; `email` is
 *  an optional secondary match signal (the BE tries email first, then
 *  LinkedIn). Mirrors BE `IdentifierItem`. */
export interface CrmStatusIdentifier {
  linkedin_url: string;
  email?: string | null;
}

/** Mirrors BE `IdentifierBudgetSummary`: like `CrmStatusBudget` but the errored
 *  set is keyed by IDENTIFIER (normalized LinkedIn URL) instead of lead id. */
export interface CrmStatusIdentifierBudget {
  used: number;
  limit: number;
  /** Identifier KEYS the BE could not resolve within budget — render the
   *  subtle "status unavailable, retry" state, NEVER "not in CRM". */
  errored_identifier_keys: string[];
}

/** Mirrors BE `StatusByIdentifierResponse`. `statuses` is keyed by the
 *  normalized identifier key (BE: `normalize_linkedin_url`). */
export interface CrmStatusByIdentifierResponse {
  statuses: Record<string, CrmStatus>;
  budget: CrmStatusIdentifierBudget;
}

export interface LookupHubSpotContactsResponse {
  task_id: string;
  message?: string;
}

// The CRM Admin settings API is flat on the wire (create_contacts_enabled,
// create_trigger, primary_connection_user_id, ...) but the FE models it nested
// (create_contacts: {...}, primary_connection_user: {...}). Map flat -> nested
// on read; putCrmSettings does the inverse before sending.
const toCrmSettings = (raw: Record<string, unknown>): CrmSettings => ({
  crm_type: raw.crm_type as CrmSettings['crm_type'],
  account_id: (raw.account_id as string) ?? '',
  primary_connection_user: raw.primary_connection_user_id
    ? {
        id: raw.primary_connection_user_id as string,
        // Prefer the resolved display name; fall back to email so the
        // "Connected via X" attribution always has something readable.
        name:
          (raw.primary_connection_user_name as string) ||
          (raw.primary_connection_user_email as string) ||
          '',
        email: (raw.primary_connection_user_email as string) ?? '',
      }
    : null,
  create_contacts: {
    enabled: Boolean(raw.create_contacts_enabled),
    trigger:
      (raw.create_trigger as CrmSettings['create_contacts']['trigger']) ??
      'on_enrichment',
    record_type:
      (raw.record_type as CrmSettings['create_contacts']['record_type']) ??
      'Contact',
    skip_if_no_account: Boolean(raw.skip_if_no_account),
  },
  create_accounts_independently: Boolean(raw.create_accounts_independently),
  create_accounts_trigger:
    (raw.create_accounts_trigger as CrmSettings['create_accounts_trigger']) ??
    'on_enrichment',
  account_write_mode:
    (raw.account_write_mode as CrmSettings['account_write_mode']) ??
    'create_and_update',
  // NULL (rows predating the column) hydrates as 'safe' so the UI always renders
  // a concrete preset; the backend coalesces NULL -> safe on the write side too.
  write_preset: (raw.write_preset as CrmSettings['write_preset']) ?? 'safe',
  bypass_duplicate_rules: Boolean(raw.bypass_duplicate_rules),
  // Admin toggle for the extension's in-CRM Enrich button. Default ON — only an
  // explicit false disables it (undefined/true → true). Top-level flat boolean,
  // like bypass_duplicate_rules (NOT under the safety block).
  in_crm_enrichment_enabled: raw.in_crm_enrichment_enabled !== false,
  match_keys: Array.isArray(raw.match_keys)
    ? (raw.match_keys as CrmMatchKey[])
    : null,
  safety: {
    // Safety guards default ON server-side; mirror that when a field is
    // absent so a fresh settings object reflects the protective default.
    protect_owned_records:
      raw.protect_owned_records === undefined
        ? true
        : Boolean(raw.protect_owned_records),
    protect_open_deals:
      raw.protect_open_deals === undefined
        ? true
        : Boolean(raw.protect_open_deals),
    allow_owner_reassign: Boolean(raw.allow_owner_reassign),
    protected_lifecycle_stages: Array.isArray(raw.protected_lifecycle_stages)
      ? (raw.protected_lifecycle_stages as string[])
      : null,
    do_not_sync_field: (raw.do_not_sync_field as string | null) ?? null,
  },
  owner_assignment: {
    // CP-14: update_contact_owner defaults TRUE server-side (owner flows
    // through the policy); mirror that when the field is absent so a fresh
    // settings object reflects the default-on behavior.
    update_contact_owner:
      raw.update_contact_owner === undefined
        ? true
        : Boolean(raw.update_contact_owner),
    update_account_owner: Boolean(raw.update_account_owner),
    // null is meaningful here ("derive from legacy allow_owner_reassign"); keep
    // it null rather than coercing so the UI can do the legacy derivation.
    owner_reassign_policy:
      (raw.owner_reassign_policy as CrmSettings['owner_assignment']['owner_reassign_policy']) ??
      null,
    fallback_owner_crm_id: (raw.fallback_owner_crm_id as string | null) ?? null,
  },
  last_edited_by: raw.last_edited_by_user_id
    ? {
        user_id: raw.last_edited_by_user_id as string,
        name: '',
        at: (raw.last_edited_at as string) ?? '',
      }
    : null,
  crm_sync_v2_enabled:
    raw.crm_sync_v2_enabled === undefined || raw.crm_sync_v2_enabled === null
      ? null
      : Boolean(raw.crm_sync_v2_enabled),
});

const expectedOrganizationHeaders = (organizationId?: string) =>
  organizationId ? { 'X-Cleanlist-Expected-Org': organizationId } : undefined;

export const crmApi = createApi({
  reducerPath: 'crmApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'CrmOptions',
    'CrmConnections',
    'MartechLists',
    'MartechMembers',
    'HubSpotProperties',
    'SalesforceProperties',
    'FieldMappingTemplates',
    'SmartColumnResults',
    'LemlistProperties',
    'AttioProperties',
    'AttioAttributes',
    'AttioLists',
    'AttioMetadata',
    'PipedriveProperties',
    'LemlistCampaigns',
    'CrmSettings',
    'CrmUsers',
    'CrmStatusOptions',
    'CrmUserMappings',
    'CrmSyncLog',
    'CrmSyncStatus',
    'CrmCompanySyncStatus',
    // [FE-D] rich status popover
    'CrmRichStatus',
    // [CP-11] rich status popover by identifier (search + directory rows)
    'CrmRichStatusByIdentifier',
    'CrmUsage',
    // CLE-560 sync profiles
    'SyncProfiles',
  ],
  endpoints: (builder) => ({
    getCrmOptions: builder.query<
      CrmOption[],
      { sfSandbox?: boolean; organizationId: string }
    >({
      query: (params) => {
        const sfSandbox = params.sfSandbox || false;
        return {
          url: `/crm/options${sfSandbox ? '?sf_sandbox=true' : ''}`,
          headers: expectedOrganizationHeaders(params.organizationId),
        };
      },
      // `{ organizationId }` and `{ sfSandbox: false, organizationId }` fetch
      // the same URL; key them the same so the app shell loads it once.
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${queryArgs.organizationId},${Boolean(queryArgs.sfSandbox)})`,
      providesTags: (_result, _err, { organizationId }) => [
        { type: 'CrmOptions' as const, id: organizationId },
      ],
      async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
        const attioWorkspace = (options: CrmOption[] | undefined) =>
          options?.find((option) => option.type === 'attio')
            ?.attio_workspace_id;
        const before = crmApi.endpoints.getCrmOptions.select(args)(
          getState() as never
        ).data;
        try {
          const { data } = await queryFulfilled;
          // Attio's fields and lists belong to its workspace: reload them
          // only when a reconnect landed on another one.
          if (before && attioWorkspace(before) !== attioWorkspace(data))
            dispatch(
              crmApi.util.invalidateTags([
                { type: 'AttioMetadata', id: args.organizationId },
              ])
            );
        } catch {
          // A failed options read says nothing about the connection.
        }
      },
    }),

    getCrmConnections: builder.query<unknown, { organizationId: string }>({
      query: (params) => ({
        url: '/crm/connections',
        headers: expectedOrganizationHeaders(params.organizationId),
      }),
      providesTags: (_result, _err, { organizationId }) => [
        { type: 'CrmConnections' as const, id: organizationId },
      ],
    }),

    removeCrmConnection: builder.mutation<
      RemoveCrmConnectionResponse,
      { connectionType: string; organizationId: string }
    >({
      query: ({ connectionType, organizationId }) => ({
        url: `/crm/connections/${connectionType}`,
        method: 'DELETE',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      invalidatesTags: (_result, _err, { connectionType, organizationId }) => [
        { type: 'CrmOptions' as const, id: organizationId },
        { type: 'CrmConnections' as const, id: organizationId },
        ...(connectionType === 'attio'
          ? [{ type: 'AttioMetadata' as const, id: organizationId }]
          : []),
      ],
    }),

    migrateLeadsToCrm: builder.mutation<
      LeadMigrationResponse,
      MigrateLeadsToCrmRequest
    >({
      query: ({
        lead_ids,
        connection_type,
        field_mapping,
        lead_list_id,
        object_type,
        campaign_id,
        operation,
        match_key,
        preset,
        override_record_guards,
      }) => ({
        url: '/crm/migrate-leads',
        method: 'POST',
        body: {
          lead_ids,
          connection_type,
          field_mapping,
          lead_list_id,
          object_type,
          campaign_id,
          operation,
          match_key,
          preset,
          override_record_guards,
        },
      }),
      // HTTP 200 can contain partial success. Promote only rows whose response
      // confirms a completed write and carries the durable CRM record id.
      async onQueryStarted(
        { connection_type },
        { dispatch, getState, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;
          if (!CRM_STATUS_CRMS.has(connection_type)) {
            return; // getCrmStatus only covers these CRMs
          }
          const crm = connection_type as CrmStatusCrm;
          const confirmed = confirmedCrmMigrations(data);
          if (confirmed.length === 0) return;
          const confirmedAt = new Date().toISOString();
          const cacheArgs = { crm, leadIds: [] };
          const patchConfirmed = () => {
            dispatch(
              crmApi.util.updateQueryData(
                'getCrmStatus',
                cacheArgs,
                (draft) => {
                  const patchedIds = new Set<string>();
                  for (const { leadId, crmRecordId } of confirmed) {
                    const entry = draft.statuses[leadId];
                    draft.statuses[leadId] = migrationProofStatus(
                      crm,
                      crmRecordId,
                      confirmedAt,
                      entry
                    );
                    patchedIds.add(leadId);
                  }
                  draft.budget.errored_lead_ids =
                    draft.budget.errored_lead_ids.filter(
                      (leadId) => !patchedIds.has(leadId)
                    );
                }
              )
            );
          };

          patchConfirmed();

          // If the first status request is still pending, the patch above has
          // no data to update. Attach to that same CRM-keyed request, then
          // reapply the write receipt after its older response lands.
          const state = getState() as Parameters<
            ReturnType<typeof crmApi.endpoints.getCrmStatus.select>
          >[0];
          const cached = crmApi.endpoints.getCrmStatus.select(cacheArgs)(state);
          const hasMissingConfirmed = confirmed.some(
            ({ leadId }) => cached.data?.statuses[leadId] === undefined
          );
          if (hasMissingConfirmed && cached.status === 'pending') {
            await dispatch(
              crmApi.endpoints.getCrmStatus.initiate(cacheArgs, {
                subscribe: false,
              })
            ).unwrap();
            patchConfirmed();
          }
          // Mutation tag invalidation can run before this fulfilled callback.
          // Invalidate after installing the proof so its detail fetch sees it.
          dispatch(crmApi.util.invalidateTags(['CrmRichStatus']));
        } catch {
          // A failed push or status request leaves the cache untouched.
        }
      },
      // Pushing records changes their sync state; refresh the per-CRM
      // sync-status columns wherever they are mounted. CrmRichStatus [FE-D]
      // refreshes the hover/click status popover after an "Update Contact".
      invalidatesTags: [
        'CrmSyncStatus',
        'CrmCompanySyncStatus',
        'CrmRichStatus',
        'CrmRichStatusByIdentifier',
      ],
    }),

    // Backfill an entire list to the CRM (manual "sync everything" action).
    backfillListToCrm: builder.mutation<
      LeadMigrationResponse,
      BackfillListToCrmRequest
    >({
      query: (body) => ({
        url: '/crm/backfill-list',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        'CrmSyncStatus',
        'CrmCompanySyncStatus',
        'CrmRichStatus',
        'CrmRichStatusByIdentifier',
      ],
    }),

    // ------------------------------------------------------------------
    // Per-CRM sync-status lookups (drive the Salesforce/HubSpot columns).
    // Both endpoints take the FULL id set and chunk into <=500-item POSTs
    // internally so callers get a single merged cache entry per surface.
    // ------------------------------------------------------------------

    getCrmSyncStatus: builder.query<
      CrmSyncStatusResponse,
      { leadIds: string[] }
    >({
      async queryFn({ leadIds }, _api, _extraOptions, fetchWithBQ) {
        const statuses: CrmSyncStatusResponse['statuses'] = {};
        for (let i = 0; i < leadIds.length; i += CRM_SYNC_STATUS_BATCH_SIZE) {
          const chunk = leadIds.slice(i, i + CRM_SYNC_STATUS_BATCH_SIZE);
          const result = await fetchWithBQ({
            url: '/crm/status/sync',
            method: 'POST',
            body: { lead_ids: chunk },
          });
          if (result.error) return { error: result.error };
          Object.assign(
            statuses,
            (result.data as CrmSyncStatusResponse | undefined)?.statuses ?? {}
          );
        }
        return { data: { statuses } };
      },
      providesTags: ['CrmSyncStatus'],
    }),

    getCrmCompanySyncStatus: builder.query<
      CrmSyncStatusResponse,
      { domains: string[] }
    >({
      async queryFn({ domains }, _api, _extraOptions, fetchWithBQ) {
        const statuses: CrmSyncStatusResponse['statuses'] = {};
        for (let i = 0; i < domains.length; i += CRM_SYNC_STATUS_BATCH_SIZE) {
          const chunk = domains.slice(i, i + CRM_SYNC_STATUS_BATCH_SIZE);
          const result = await fetchWithBQ({
            url: '/crm/status/sync/companies',
            method: 'POST',
            body: { domains: chunk },
          });
          // The companies endpoint may land after the leads one. Degrade
          // gracefully: callers render dim "unknown" cells on any error.
          if (result.error) return { error: result.error };
          Object.assign(
            statuses,
            (result.data as CrmSyncStatusResponse | undefined)?.statuses ?? {}
          );
        }
        return { data: { statuses } };
      },
      providesTags: ['CrmCompanySyncStatus'],
    }),

    // ──────────────────────────────────────────────────────────────────
    // [FE-D] Rich per-lead CRM status (drives the hover/click popover).
    // POST /crm/status { crm, lead_ids } -> { statuses, budget }. Chunked at
    // CRM_STATUS_BATCH_SIZE (small — this endpoint is heavier than
    // sync-status). The per-chunk budgets are merged so callers see a single
    // accumulated budget (errored_lead_ids union, used/limit from the last
    // chunk the BE reported). Keyed per CRM via serializeQueryArgs so
    // Salesforce and HubSpot keep separate cache entries.
    // ──────────────────────────────────────────────────────────────────
    getCrmStatus: builder.query<
      CrmStatusResponse,
      { crm: CrmStatusCrm; leadIds: string[] }
    >({
      async queryFn({ crm, leadIds }, api, _extraOptions, fetchWithBQ) {
        // De-amplify infinite scroll: forceRefetch re-invokes this with the
        // FULL accumulated lead set on every page, and serializeQueryArgs keys
        // only on `crm`, so without this we'd re-POST every already-resolved
        // lead each page (ceil(N/50) requests × pages). Read the merged cache
        // entry for this CRM and fetch missing statuses or pending write
        // proofs; `merge` unions the delta back in. (Errored leads are not
        // in `statuses`, so they retry — bounded and desirable.)
        const cached = crmApi.endpoints.getCrmStatus.select({ crm, leadIds })(
          api.getState() as Parameters<
            ReturnType<typeof crmApi.endpoints.getCrmStatus.select>
          >[0]
        )?.data;
        const known = cached?.statuses ?? {};
        // A write receipt confirms membership but still needs rich details.
        // Reuse this batched invalidation fetch; once the lookup fails, leave
        // that proof for the explicit Retry path instead of retrying each page.
        const unresolvedIds = new Set(cached?.budget.errored_lead_ids ?? []);
        const toFetch = leadIds.filter(
          (id) =>
            known[id] === undefined ||
            (isConfirmedMigrationProof(known[id]) &&
              known[id].stale &&
              !unresolvedIds.has(id))
        );

        const statuses: CrmStatusResponse['statuses'] = {};
        const erroredLeadIds = new Set<string>();
        const budget: CrmStatusBudget = {
          used: cached?.budget.used ?? 0,
          limit: cached?.budget.limit ?? 0,
          errored_lead_ids: [],
        };
        let fetchError: FetchBaseQueryError | undefined;
        for (let i = 0; i < toFetch.length; i += CRM_STATUS_BATCH_SIZE) {
          const chunk = toFetch.slice(i, i + CRM_STATUS_BATCH_SIZE);
          // Auto-retry transient failures (bounded) BEFORE erroring the whole
          // batch into the popover's "couldn't load status" card.
          const result = await fetchStatusChunkWithRetry(fetchWithBQ, {
            url: '/crm/status',
            method: 'POST',
            body: { crm, lead_ids: chunk },
          });
          if (result.error) {
            fetchError = result.error;
            break;
          }
          const data = result.data as CrmStatusResponse | undefined;
          Object.assign(statuses, data?.statuses ?? {});
          if (data?.budget) {
            // `used`/`limit` are the org's cumulative daily total returned by
            // the BE after each call, so take the max across chunks rather than
            // letting the last (or a no-op) chunk understate it.
            budget.used = Math.max(budget.used, data.budget.used);
            budget.limit = Math.max(budget.limit, data.budget.limit);
            for (const id of data.budget.errored_lead_ids ?? []) {
              erroredLeadIds.add(id);
            }
          }
        }
        // A sync or explicit refresh can update a row while these chunks are
        // loading. Preserve that newer row, including same-record writes with
        // equal timestamps, and discard errors from the superseded lookup.
        const latest =
          crmApi.endpoints.getCrmStatus.select({ crm, leadIds })(
            api.getState() as Parameters<
              ReturnType<typeof crmApi.endpoints.getCrmStatus.select>
            >[0]
          ).data?.statuses ?? {};
        if (fetchError) {
          // Keep completed details for current write proofs, and expose Retry
          // for unresolved, failed, or unattempted proofs. Ordinary page loads
          // retain their existing all-or-nothing endpoint error behavior.
          const completedProofStatuses: CrmStatusResponse['statuses'] = {};
          const failedProofIds: string[] = [];
          for (const id of toFetch) {
            if (
              !isConfirmedMigrationProof(known[id]) ||
              latest[id] !== known[id]
            ) {
              continue;
            }
            const completed = statuses[id];
            if (
              completed?.exists_in_crm &&
              completed.record_id === known[id].record_id &&
              !completed.stale &&
              !erroredLeadIds.has(id)
            ) {
              completedProofStatuses[id] = completed;
            } else {
              failedProofIds.push(id);
            }
          }
          if (
            failedProofIds.length > 0 ||
            Object.keys(completedProofStatuses).length > 0
          ) {
            api.dispatch(
              crmApi.util.updateQueryData(
                'getCrmStatus',
                { crm, leadIds },
                (draft) => {
                  mergeCrmStatusResponse(draft, {
                    statuses: completedProofStatuses,
                    budget: { ...budget, errored_lead_ids: failedProofIds },
                  });
                }
              )
            );
          }
          return { error: fetchError };
        }
        // A successful batch can still omit a requested proof. Keep Retry
        // available for it, subject to the same newer-row guard below.
        for (const id of toFetch) {
          if (isConfirmedMigrationProof(known[id]) && !statuses[id]) {
            erroredLeadIds.add(id);
          }
        }
        for (const id of new Set([
          ...Object.keys(statuses),
          ...erroredLeadIds,
        ])) {
          if (latest[id] && latest[id] !== known[id]) {
            delete statuses[id];
            erroredLeadIds.delete(id);
          }
        }
        budget.errored_lead_ids = Array.from(erroredLeadIds);
        return { data: { statuses, budget } };
      },
      // One cache entry per CRM. leadIds order/dupes are normalized by the
      // hook before it ever reaches here, so the args are already canonical.
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${queryArgs.crm})`,
      // A given CRM's cache entry holds whatever lead set was last requested;
      // merge incoming pages in so paginating accumulates rather than
      // replaces (re-entry is then free from cache).
      merge: (current, incoming) => {
        mergeCrmStatusResponse(current, incoming);
      },
      // Refetch only when the requested lead set actually changes.
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.crm !== previousArg?.crm ||
        currentArg?.leadIds.join(',') !== previousArg?.leadIds.join(','),
      providesTags: ['CrmRichStatus'],
      // Keep the (heavy) rich-status cache around long after the route
      // unmounts so navigating away and back doesn't re-POST every visible
      // lead. Without this it inherits RTK's 60s default and the cache is
      // GC'd shortly after the list unmounts → a full re-fetch on re-entry.
      // Matches the sibling 24h endpoints in this file.
      keepUnusedDataFor: 60 * 60 * 24,
    }),

    // ──────────────────────────────────────────────────────────────────
    // [perf] Forced single-lead rich-status REFRESH (per-row "Retry").
    // POST /crm/status { crm, lead_ids:[leadId], refresh:true } — the
    // `refresh:true` flag tells the BE to bypass its server-side cache and
    // re-resolve this one lead live. On success we MANUALLY patch the merged
    // `getCrmStatus(crm)` cache entry: overwrite the lead's status with the
    // fresh value AND remove it from `budget.errored_lead_ids`.
    //
    // Why not just invalidate the tag? Two reasons the old Retry was a no-op:
    //   1. invalidateTags refetches via the SAME queryFn, whose delta-fetch
    //      skips leads already present in `statuses` — an errored lead IS
    //      already absent, so it retries, but WITHOUT `refresh:true` the BE
    //      re-serves the same cached (errored) result.
    //   2. the `merge` fn only UNIONS `errored_lead_ids` and never subtracts,
    //      so a lead stays "errored" forever even after a forced success.
    // Patching the cache directly is the decisive fix for (2).
    // ──────────────────────────────────────────────────────────────────
    refreshCrmStatusForLead: builder.mutation<
      CrmStatusResponse,
      { crm: CrmStatusCrm; leadId: string }
    >({
      query: ({ crm, leadId }) => ({
        url: '/crm/status',
        method: 'POST',
        body: { crm, lead_ids: [leadId], refresh: true },
      }),
      async onQueryStarted(
        { crm, leadId },
        { dispatch, getState, queryFulfilled }
      ) {
        const cacheArgs = { crm, leadIds: [] };
        const readCachedStatus = () =>
          crmApi.endpoints.getCrmStatus.select(cacheArgs)(
            getState() as Parameters<
              ReturnType<typeof crmApi.endpoints.getCrmStatus.select>
            >[0]
          ).data?.statuses[leadId];
        const statusAtRequestStart = readCachedStatus();
        try {
          const { data } = await queryFulfilled;
          const fresh = data?.statuses?.[leadId];
          // Treat the retry as RESOLVED only when it returned a status AND the BE
          // did NOT re-report the lead as errored. A re-FAILED retry (still
          // over-budget / timed-out / errored) serves a STALE snapshot — so
          // `fresh` is truthy — while keeping the lead in errored_lead_ids;
          // clearing the flag then would fabricate a resolved / "not in CRM"
          // state and drop the Retry affordance. Gate on BOTH.
          const stillErrored =
            data?.budget?.errored_lead_ids?.includes(leadId) ?? false;
          if (!fresh || (stillErrored && !isConfirmedMigrationProof(fresh))) {
            return;
          }
          // Redux keeps unchanged row objects by reference. Reject this older
          // read if the row changed, even if a newer sync's proof has already
          // become a normal rich status after enrichment. Equal timestamps and
          // record IDs must not hide an intervening update.
          const currentStatus = readCachedStatus();
          if (currentStatus !== statusAtRequestStart) {
            return;
          }
          // PATCH ONLY — never SEED. `getCrmStatus`'s serializeQueryArgs keys on
          // `crm`, so the entry is shared across the whole page. When the initial
          // batch query failed entirely there is NO entry; updateQueryData
          // correctly no-ops (the page stays in the honest `statusUnknown`
          // state). Do NOT upsert a single-lead response to seed it: that flips
          // the shared entry to fulfilled and every OTHER still-unresolved lead
          // would read undefined and render a FALSE "not in CRM". (Recovering a
          // total-batch failure needs a full refetch, tracked separately.)
          dispatch(
            crmApi.util.updateQueryData('getCrmStatus', cacheArgs, (draft) => {
              mergeCrmStatusResponse(
                draft,
                {
                  statuses: { [leadId]: fresh },
                  budget: data.budget,
                },
                !stillErrored && !fresh.stale ? leadId : undefined
              );
            })
          );
        } catch {
          // On failure leave the cache untouched: the lead stays errored and
          // the popover keeps its honest "status unavailable, retry" state.
        }
      },
    }),

    // ──────────────────────────────────────────────────────────────────
    // [CP-11] Rich per-IDENTIFIER CRM status (people search + directory rows
    // with no persisted Lead.id). POST /crm/status/by-identifier
    // { crm, identifiers:[{ linkedin_url, email? }] } -> { statuses, budget }.
    //
    // Mirrors getCrmStatus exactly: chunked at CRM_STATUS_BATCH_SIZE (heavier
    // than sync-status), per-chunk budgets merged into one accumulated budget
    // (errored_identifier_keys union; used/limit from the last chunk reported),
    // keyed per CRM via serializeQueryArgs so SF/HS keep separate cache entries,
    // and pages merged so paginate accumulates rather than replaces.
    //
    // statuses come back keyed by the BE's normalized LinkedIn key
    // (normalize_linkedin_url); the FE looks them up via the matching
    // crmStatusIdentifierKey() normalizer.
    //
    // AppSumo fence: the endpoint lives on the AppSumo-fenced CrmStatusRouter,
    // so it 403s for AppSumo-only orgs. A 403 (or any error) is swallowed into
    // a clean EMPTY result here — the hook degrades to a hidden trigger; it is
    // NEVER surfaced as a thrown error or a "not in CRM" negative.
    // ──────────────────────────────────────────────────────────────────
    getCrmStatusByIdentifier: builder.query<
      CrmStatusByIdentifierResponse,
      { crm: CrmStatusCrm; identifiers: CrmStatusIdentifier[] }
    >({
      async queryFn({ crm, identifiers }, api, _extraOptions, fetchWithBQ) {
        // De-amplify infinite scroll (same rationale as getCrmStatus): only
        // resolve identifiers we don't already have a status for. Each
        // identifier's `linkedin_url` is the normalized key the BE echoes its
        // `statuses` map by, so a cache hit on that key means "already known".
        const cached = crmApi.endpoints.getCrmStatusByIdentifier.select({
          crm,
          identifiers,
        })(
          api.getState() as Parameters<
            ReturnType<typeof crmApi.endpoints.getCrmStatusByIdentifier.select>
          >[0]
        )?.data;
        const known = cached?.statuses ?? {};
        const toFetch = identifiers.filter(
          (id) => known[id.linkedin_url] === undefined
        );

        const statuses: CrmStatusByIdentifierResponse['statuses'] = {};
        const erroredKeys = new Set<string>();
        const budget: CrmStatusIdentifierBudget = {
          used: cached?.budget.used ?? 0,
          limit: cached?.budget.limit ?? 0,
          errored_identifier_keys: [],
        };
        for (let i = 0; i < toFetch.length; i += CRM_STATUS_BATCH_SIZE) {
          const chunk = toFetch.slice(i, i + CRM_STATUS_BATCH_SIZE);
          // Auto-retry transient failures (bounded). The 403 AppSumo fence is
          // NOT transient, so it reaches the swallow below on the 1st attempt.
          const result = await fetchStatusChunkWithRetry(fetchWithBQ, {
            url: '/crm/status/by-identifier',
            method: 'POST',
            body: { crm, identifiers: chunk },
          });
          if (result.error) {
            // AppSumo fence (403): the org genuinely can't use this endpoint, so
            // swallow into a clean EMPTY "unavailable" state (the trigger hides —
            // never a crash or a fabricated "not in CRM"). Any OTHER failure
            // (network / 5xx / timeout) must SURFACE as isError so the hook
            // degrades to statusUnknown and the popover offers a Retry, instead
            // of swallowing it into a false "not in CRM yet" (WF#3/#6).
            if (result.error.status === 403) {
              return {
                data: {
                  statuses: {},
                  budget: { used: 0, limit: 0, errored_identifier_keys: [] },
                },
              };
            }
            return { error: result.error };
          }
          const data = result.data as CrmStatusByIdentifierResponse | undefined;
          Object.assign(statuses, data?.statuses ?? {});
          if (data?.budget) {
            budget.used = Math.max(budget.used, data.budget.used);
            budget.limit = Math.max(budget.limit, data.budget.limit);
            for (const key of data.budget.errored_identifier_keys ?? []) {
              erroredKeys.add(key);
            }
          }
        }
        budget.errored_identifier_keys = Array.from(erroredKeys);
        return { data: { statuses, budget } };
      },
      // One cache entry per CRM; identifiers are pre-normalized/sorted by the
      // hook so the args are already canonical.
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${queryArgs.crm})`,
      // Merge incoming pages so paginating accumulates (re-entry is free).
      merge: (current, incoming) => {
        Object.assign(current.statuses, incoming.statuses);
        // A key this page resolved is no longer errored. A pure union kept a
        // once-failed row on "couldn't load" for the cache's whole 24h life even
        // after a later page fetched it cleanly. Re-reported keys stay errored.
        const errored = new Set([
          ...current.budget.errored_identifier_keys.filter(
            (key) => !incoming.statuses[key]
          ),
          ...incoming.budget.errored_identifier_keys,
        ]);
        current.budget = {
          used: Math.max(current.budget.used, incoming.budget.used),
          limit: Math.max(current.budget.limit, incoming.budget.limit),
          errored_identifier_keys: Array.from(errored),
        };
      },
      // Refetch only when the requested (crm, identifier set) actually changes.
      // Key on BOTH linkedin_url AND email — a row can be matched by email
      // alone (no LinkedIn), so omitting email would skip the refetch and show
      // a stale/missing status for email-only rows.
      forceRefetch: ({ currentArg, previousArg }) => {
        const key = (a?: { identifiers: CrmStatusIdentifier[] }) =>
          a?.identifiers
            .map((id) => `${id.linkedin_url ?? ''}|${id.email ?? ''}`)
            .join(',');
        return (
          currentArg?.crm !== previousArg?.crm ||
          key(currentArg) !== key(previousArg)
        );
      },
      providesTags: ['CrmRichStatusByIdentifier'],
      // Same rationale as getCrmStatus: keep this heavy per-identifier cache
      // alive across route unmount/remount so the popover doesn't re-POST on
      // re-entry. Matches the sibling 24h endpoints in this file.
      keepUnusedDataFor: 60 * 60 * 24,
    }),

    // ──────────────────────────────────────────────────────────────────
    // [perf] Forced single-IDENTIFIER rich-status REFRESH — the identifier
    // analog of refreshCrmStatusForLead, for People-search / directory rows
    // (CP-11) that have no Lead.id and so cannot use the lead-keyed Retry.
    // POST /crm/status/by-identifier { refresh:true } bypasses the BE cache and
    // re-resolves this identifier live; on success we patch the merged
    // getCrmStatusByIdentifier(crm) entry: overwrite the resolved status(es)
    // AND drop their keys from budget.errored_identifier_keys (the union `merge`
    // never subtracts). Without this, the 24h keepUnusedDataFor would strand an
    // identifier-errored row with no manual recovery on a static view.
    // ──────────────────────────────────────────────────────────────────
    refreshCrmStatusByIdentifier: builder.mutation<
      CrmStatusByIdentifierResponse,
      { crm: CrmStatusCrm; identifier: CrmStatusIdentifier }
    >({
      query: ({ crm, identifier }) => ({
        url: '/crm/status/by-identifier',
        method: 'POST',
        body: { crm, identifiers: [identifier], refresh: true },
      }),
      async onQueryStarted({ crm }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const fresh = data?.statuses ?? {};
          // A re-FAILED retry serves a STALE status (key present in `fresh`) yet
          // re-reports the key as errored — exclude those keys so we don't
          // fabricate a resolved state and drop Retry (mirrors the lead
          // refresh's stillErrored gate). Only genuinely-resolved keys are
          // patched + cleared from the errored set.
          const stillErrored = new Set<string>(
            data?.budget?.errored_identifier_keys ?? []
          );
          const resolvedKeys = Object.keys(fresh).filter(
            (key) => !stillErrored.has(key)
          );
          // A 200 that resolved NOTHING leaves the cache untouched so the row
          // keeps its honest "unavailable, retry" state.
          if (resolvedKeys.length === 0) return;
          dispatch(
            // serializeQueryArgs keys getCrmStatusByIdentifier ONLY on `crm`, so
            // identifiers:[] resolves to the same live entry (same trick as the
            // lead refresh passing leadIds:[]).
            crmApi.util.updateQueryData(
              'getCrmStatusByIdentifier',
              { crm, identifiers: [] },
              (draft) => {
                for (const key of resolvedKeys) {
                  draft.statuses[key] = fresh[key];
                }
                draft.budget.errored_identifier_keys =
                  draft.budget.errored_identifier_keys.filter(
                    (key) => !resolvedKeys.includes(key)
                  );
              }
            )
          );
        } catch {
          // Leave the cache untouched on failure: the identifier stays errored
          // and the popover keeps its honest "status unavailable, retry" state.
        }
      },
    }),

    // ------------------------------------------------------------------
    // CRM-based exclusion set (FE-E). Resolves the identifier set for one
    // CRM condition so the People search can inject it into the Crustdata
    // exclude inputs. POST (not GET) because force_refresh + the
    // parameterized conditions are awkward as query params and the result
    // is recomputed server-side. Not cached via a tag — the freshness
    // contract lives in the response (fetched_at / stale), and the
    // force_refresh flag is the explicit cache-bust. RTK Query still
    // dedupes identical in-flight POSTs.
    // ------------------------------------------------------------------
    getExclusionSet: builder.mutation<
      CrmExclusionSetResponse,
      CrmExclusionSetRequest
    >({
      query: ({
        organizationId,
        crm,
        condition,
        force_refresh,
        supports_indexed,
      }) => ({
        url: '/crm/exclusion-set',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
        body: {
          crm,
          condition,
          ...(force_refresh ? { force_refresh: true } : {}),
          ...(supports_indexed ? { supports_indexed: true } : {}),
        },
      }),
    }),

    getHubSpotProperties: builder.query<
      HubSpotPropertiesResponse,
      {
        objectType?: string;
        /** ``exclusion`` lists only the fields a ``field:`` exclusion can
         *  name, each with its ``exclusion_ops``; ``filter`` lists every
         *  visible property a trigger can search on, read-only ones too. */
        purpose?: 'exclusion' | 'filter';
        organizationId: string;
      }
    >({
      // object_type selects the HubSpot object schema (contact vs company).
      // Omitted -> contact (back-compat with existing callers passing undefined).
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.objectType) search.set('object_type', params.objectType);
        if (params?.purpose) search.set('purpose', params.purpose);
        const qs = search.toString();
        return {
          url: qs ? `/crm/hubspot/properties?${qs}` : '/crm/hubspot/properties',
          headers: expectedOrganizationHeaders(params?.organizationId),
        };
      },
      // undefined, {}, and { objectType: undefined } all hit the same backend
      // (contact). Canonicalize so they share one cache entry instead of three.
      // The exclusion list is a different payload, so it keys apart.
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const args = queryArgs as {
          objectType?: string;
          purpose?: 'exclusion' | 'filter';
          organizationId: string;
        };
        return `${endpointName}(${args.organizationId}:${args.objectType ?? 'contact'}${args.purpose ? `:${args.purpose}` : ''})`;
      },
      providesTags: (
        _result,
        _err,
        { objectType, purpose, organizationId }
      ) => [
        {
          type: 'HubSpotProperties' as const,
          id: `${organizationId}:${objectType ?? 'contact'}${purpose ? `:${purpose}` : ''}`,
        },
      ],
      keepUnusedDataFor: 60 * 60 * 24,
    }),

    refreshHubSpotProperties: builder.mutation<
      HubSpotPropertiesResponse,
      { objectType?: string; organizationId: string }
    >({
      query: (params) => ({
        url: '/crm/hubspot/properties',
        headers: expectedOrganizationHeaders(params?.organizationId),
        params: {
          refresh: true,
          ...(params && params.objectType
            ? { object_type: params.objectType }
            : {}),
        },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const args = arg;
          const objectType = args.objectType;
          dispatch(
            crmApi.util.updateQueryData(
              'getHubSpotProperties',
              {
                ...(objectType ? { objectType } : {}),
                organizationId: args.organizationId,
              },
              (draft) => {
                draft.properties = data.properties;
                draft.last_refreshed = data.last_refreshed;
              }
            )
          );
        } catch {
          // noop
        }
      },
    }),

    getFieldMappingTemplates: builder.query<
      FieldMappingTemplateListResponse,
      { crmType?: string; objectType?: string; organizationId: string }
    >({
      query: (params) => {
        const crmType = params?.crmType || 'hubspot';
        const objectType = params?.objectType || 'contact';
        return {
          url: `/crm/field-mapping-templates?crm_type=${crmType}&object_type=${objectType}`,
          headers: expectedOrganizationHeaders(params?.organizationId),
        };
      },
      providesTags: (
        _result,
        _err,
        { crmType = 'hubspot', objectType = 'contact', organizationId }
      ) => [
        {
          type: 'FieldMappingTemplates' as const,
          id: `${organizationId}:${crmType}:${objectType}`,
        },
      ],
    }),

    // Persist the CRM admin Field Mapping tab as the org's DEFAULT template for
    // (crm_type, object_type) — the mapping the auto-push actually reads. This is
    // what makes configuring a mapping in the panel take effect for the sync.
    upsertDefaultFieldMapping: builder.mutation<
      FieldMappingTemplate,
      {
        organizationId: string;
        crm_type: string;
        object_type: string;
        mappings: FieldMappingItem[];
      }
    >({
      query: ({ organizationId, ...body }) => ({
        url: '/crm/field-mapping-templates/upsert-default',
        method: 'POST',
        body,
        headers: expectedOrganizationHeaders(organizationId),
        keepalive: true,
      }),
      invalidatesTags: (
        _result,
        _err,
        { organizationId, crm_type, object_type }
      ) => [
        {
          type: 'FieldMappingTemplates' as const,
          id: `${organizationId}:${crm_type}:${object_type}`,
        },
      ],
    }),

    getMartechLists: builder.query<
      MartechList[],
      {
        connectionType: string;
        limit?: number;
        offset?: number;
        organizationId?: string;
      }
    >({
      query: ({ connectionType, limit = 100, offset = 0, organizationId }) => ({
        url: `/crm/martech/lists?connection_type=${connectionType}&limit=${limit}&offset=${offset}`,
        ...(organizationId
          ? { headers: expectedOrganizationHeaders(organizationId) }
          : {}),
      }),
      providesTags: (_, __, { connectionType, organizationId }) => [
        {
          type: 'MartechLists',
          id: organizationId
            ? `${organizationId}:${connectionType}`
            : connectionType,
        },
      ],
    }),

    getMartechMembers: builder.query<
      MartechMember[],
      {
        listId: string;
        connectionType: string;
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ listId, connectionType, limit = 100, offset = 0 }) =>
        `/crm/martech/lists/${listId}/members?connection_type=${connectionType}&limit=${limit}&offset=${offset}`,
      providesTags: (_, __, { listId, connectionType }) => [
        { type: 'MartechMembers', id: `${connectionType}-${listId}` },
      ],
    }),

    importCrmList: builder.mutation<
      ImportCrmListResponse,
      ImportCrmListRequest
    >({
      query: ({
        connection_type,
        contacts,
        lead_list_name,
        lead_list_id,
        enrichment_type,
        crm_list_id,
        enable_sync,
        object_type,
      }) => ({
        url: '/crm/import-list',
        method: 'POST',
        body: {
          connection_type,
          contacts,
          lead_list_name,
          lead_list_id,
          enrichment_type,
          crm_list_id,
          enable_sync,
          object_type,
        },
      }),
    }),

    toggleCrmPoll: builder.mutation<
      ToggleCrmPollResponse,
      { leadListId: string; enabled: boolean }
    >({
      query: ({ leadListId, enabled }) => ({
        url: `/crm/lead-lists/${leadListId}/poll/toggle`,
        method: 'POST',
        body: { enabled },
      }),
    }),

    triggerCrmPollNow: builder.mutation<TriggerCrmPollResponse, string>({
      query: (leadListId) => ({
        url: `/crm/lead-lists/${leadListId}/poll/now`,
        method: 'POST',
      }),
    }),

    lookupHubSpotContacts: builder.mutation<
      LookupHubSpotContactsResponse,
      LookupHubSpotContactsRequest
    >({
      query: ({ organizationId, lead_list_id, lead_ids, config }) => ({
        url: '/crm/hubspot/lookup-contacts',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
        body: { lead_list_id, lead_ids, config },
      }),
      invalidatesTags: ['SmartColumnResults'],
    }),

    getSalesforceProperties: builder.query<
      HubSpotPropertiesResponse,
      {
        objectType?: string;
        purpose?: 'filter' | 'mapping' | 'play_write' | 'exclusion';
        organizationId: string;
      }
    >({
      query: ({
        objectType = 'Contact',
        purpose = 'mapping',
        organizationId,
      }) => ({
        url: `/crm/salesforce/properties?object_type=${objectType}&purpose=${purpose}`,
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (
        _result,
        _err,
        { objectType = 'Contact', purpose = 'mapping', organizationId }
      ) => [
        {
          type: 'SalesforceProperties' as const,
          id: `${organizationId}:${objectType}:${purpose}`,
        },
      ],
      keepUnusedDataFor: 60 * 60 * 24,
    }),

    getSalesforceLists: builder.query<
      MartechList[],
      { objectType?: string; organizationId?: string }
    >({
      query: ({ objectType = 'Contact', organizationId }) => ({
        url: `/crm/salesforce/lists?object_type=${objectType}`,
        ...(organizationId
          ? { headers: expectedOrganizationHeaders(organizationId) }
          : {}),
      }),
      providesTags: ['MartechLists'],
    }),

    // Advanced-playbooks inbound trigger pickers (build-v3 §2f L6, FE-D).
    // Both endpoints are require_custom_playbooks-gated on the BE, so a 404
    // here for a non-flagged org is the normal dark-launch state, not an error.
    getSalesforceListViews: builder.query<
      SalesforceListView[],
      { objectType?: string }
    >({
      query: ({ objectType = 'Contact' }) =>
        `/crm/salesforce/list-views?object_type=${objectType}`,
      providesTags: (_result, _err, { objectType = 'Contact' }) => [
        { type: 'MartechLists' as const, id: `sf-listviews-${objectType}` },
      ],
    }),

    getSalesforceReports: builder.query<
      SalesforceReport[],
      { organizationId?: string } | void
    >({
      query: (args) => ({
        url: '/crm/salesforce/reports',
        ...(args?.organizationId
          ? { headers: expectedOrganizationHeaders(args.organizationId) }
          : {}),
      }),
      providesTags: [{ type: 'MartechLists' as const, id: 'sf-reports' }],
    }),

    getSalesforceListMembers: builder.query<
      MartechMember[],
      { listId: string; objectType?: string }
    >({
      query: ({ listId, objectType = 'Contact' }) =>
        `/crm/salesforce/lists/${listId}/members?object_type=${objectType}`,
      providesTags: (_, __, { listId }) => [
        { type: 'MartechMembers', id: `salesforce-${listId}` },
      ],
    }),

    lookupSalesforceContacts: builder.mutation<
      LookupHubSpotContactsResponse,
      LookupHubSpotContactsRequest
    >({
      query: ({ organizationId, lead_list_id, lead_ids, config }) => ({
        url: '/crm/salesforce/lookup-contacts',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
        body: { lead_list_id, lead_ids, config },
      }),
      invalidatesTags: ['SmartColumnResults'],
    }),

    connectLemlist: builder.mutation<
      { message: string; organization_id: string },
      { api_key: string }
    >({
      query: (body) => ({
        url: '/crm/lemlist/connect',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CrmOptions', 'CrmConnections'],
    }),

    getLemlistCampaigns: builder.query<{ id: string; name: string }[], void>({
      query: () => '/crm/lemlist/campaigns',
      providesTags: ['LemlistCampaigns'],
    }),

    // Pipedrive person fields for the export mapping (behind pipedrive_v1;
    // the backend 404s while the org's flag is off).
    getPipedriveProperties: builder.query<
      HubSpotPropertiesResponse,
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: '/crm/pipedrive/properties',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (_result, _err, { organizationId }) => [
        { type: 'PipedriveProperties' as const, id: organizationId },
      ],
      keepUnusedDataFor: 60 * 60,
    }),

    getLemlistProperties: builder.query<
      HubSpotPropertiesResponse,
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: '/crm/lemlist/properties',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (_result, _err, { organizationId }) => [
        { type: 'LemlistProperties' as const, id: organizationId },
      ],
      keepUnusedDataFor: 60 * 60 * 24,
    }),

    // The org's Attio people attributes a push can write (CLE-510), read
    // live by the API; the name shows as its two halves.
    getAttioProperties: builder.query<
      HubSpotPropertiesResponse,
      { organizationId: string; workspaceId?: string }
    >({
      query: ({ organizationId }) => ({
        url: '/crm/attio/properties',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (_result, _err, { organizationId }) => [
        { type: 'AttioProperties' as const, id: organizationId },
        { type: 'AttioMetadata' as const, id: organizationId },
      ],
      keepUnusedDataFor: 60 * 60,
    }),

    // Attio attributes for the play pickers (Look up, Update fields, company
    // Sync, exclude clauses). This route reads no flag; Export keeps
    // getAttioProperties above.
    getAttioAttributes: builder.query<
      HubSpotPropertiesResponse,
      GetAttioAttributesArgs
    >({
      query: ({ objectType, purpose, organizationId }) => ({
        url: '/crm/attio/attributes',
        params: { object_type: objectType, purpose },
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (
        _result,
        _err,
        { objectType, purpose, organizationId }
      ) => [
        {
          type: 'AttioAttributes' as const,
          id: `${organizationId}:${objectType}:${purpose}`,
        },
        { type: 'AttioMetadata' as const, id: organizationId },
      ],
      keepUnusedDataFor: 60 * 60,
    }),

    // Attio lists, optionally only those whose parent is objectType. Reads
    // no flag.
    getAttioLists: builder.query<
      AttioList[],
      {
        organizationId: string;
        workspaceId?: string;
        objectType?: AttioObjectType;
      }
    >({
      query: ({ objectType, organizationId }) => ({
        url: '/crm/attio/lists',
        params: objectType ? { object_type: objectType } : undefined,
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (_result, _err, { objectType, organizationId }) => [
        {
          type: 'AttioLists' as const,
          id: `${organizationId}:${objectType ?? 'all'}`,
        },
        { type: 'AttioMetadata' as const, id: organizationId },
      ],
    }),

    // -----------------------------------------------------------------------
    // CRM Admin Panel V1 (CLE-310)
    // -----------------------------------------------------------------------

    getCrmSettings: builder.query<
      CrmSettings,
      { crmType: CrmAdminPanelCrm; organizationId: string }
    >({
      query: ({ crmType, organizationId }) => ({
        url: `/crm/management/${crmType}/settings`,
        headers: expectedOrganizationHeaders(organizationId),
      }),
      transformResponse: (raw: Record<string, unknown>) => toCrmSettings(raw),
      providesTags: (_result, _err, { crmType, organizationId }) => [
        {
          type: 'CrmSettings' as const,
          id: `${organizationId}:${crmType}`,
        },
      ],
    }),

    putCrmSettings: builder.mutation<
      CrmSettings,
      {
        crmType: CrmAdminPanelCrm;
        organizationId: string;
        body: CrmSettingsRequest;
      }
    >({
      query: ({ crmType, organizationId, body }) => ({
        url: `/crm/management/${crmType}/settings`,
        method: 'PUT',
        headers: expectedOrganizationHeaders(organizationId),
        keepalive: true,
        // FE sends nested; the BE expects flat fields. Only include the match
        // cascades when the caller actually set them so the backend's partial
        // update leaves unsent fields untouched.
        body: {
          // General block — included ONLY when the caller actually sent it. The
          // User Mapping (owner) tab autosaves ``owner_assignment`` ALONE with no
          // ``create_contacts``, so reading ``body.create_contacts.*``
          // unconditionally threw (TypeError → the PUT never fired, surfaced as
          // "Save failed"). Gate it like the owner block below; the BE's partial
          // (model_fields_set) update leaves unsent fields untouched.
          ...(body.create_contacts !== undefined
            ? {
                create_contacts_enabled: body.create_contacts.enabled,
                create_trigger: body.create_contacts.trigger,
                record_type: body.create_contacts.record_type,
                skip_if_no_account: body.create_contacts.skip_if_no_account,
                create_accounts_independently:
                  body.create_accounts_independently,
                create_accounts_trigger: body.create_accounts_trigger,
                account_write_mode: body.account_write_mode,
                bypass_duplicate_rules: body.bypass_duplicate_rules,
                in_crm_enrichment_enabled: body.in_crm_enrichment_enabled,
              }
            : {}),
          ...(body.match_keys !== undefined
            ? { match_keys: body.match_keys }
            : {}),
          // Write-aggressiveness preset. Sent on its own when the General tab's
          // write-aggressiveness control changes (independent of create_contacts)
          // so a preset-only autosave persists via the backend's partial update
          // (model_fields_set) without clobbering other fields.
          ...(body.write_preset !== undefined
            ? { write_preset: body.write_preset }
            : {}),
          // CP-14 owner-assignment fields are flat on the wire. Only include
          // them when the caller actually set owner_assignment so the backend's
          // partial update (model_fields_set) leaves them untouched for the
          // General tab, which never sends this block.
          ...(body.owner_assignment !== undefined
            ? {
                update_contact_owner:
                  body.owner_assignment.update_contact_owner,
                update_account_owner:
                  body.owner_assignment.update_account_owner,
                owner_reassign_policy:
                  body.owner_assignment.owner_reassign_policy,
                fallback_owner_crm_id:
                  body.owner_assignment.fallback_owner_crm_id,
              }
            : {}),
          // Write-safety fields are flat on the wire too. Only include them when
          // the caller actually sent the safety block so the backend's partial
          // update (model_fields_set) leaves them untouched for tabs that don't
          // edit safety. The onQueryStarted cache patch already keys its
          // General-tab branch on ``body.safety !== undefined``; without this
          // spread those edits were checked for cache-patching but never sent.
          ...(body.safety !== undefined
            ? {
                protect_owned_records: body.safety.protect_owned_records,
                protect_open_deals: body.safety.protect_open_deals,
                protected_lifecycle_stages:
                  body.safety.protected_lifecycle_stages,
                allow_owner_reassign: body.safety.allow_owner_reassign,
                do_not_sync_field: body.safety.do_not_sync_field,
              }
            : {}),
        },
      }),
      transformResponse: (raw: Record<string, unknown>) => toCrmSettings(raw),
      // Patch the settings cache from the PUT's returned (fully-resolved)
      // settings so the form reflects the saved owner-assignment immediately —
      // the tag-invalidation refetch lands a tick later, and the section would
      // otherwise re-render from the stale cache in between (mirrors
      // updateCrmUsageCap). The invalidate still runs to refresh other views.
      //
      // SELECTIVE MERGE (cross-tab clobber guard): the General tab and the User
      // Mapping (owner-assignment) tab each autosave independently. A full
      // `Object.assign(draft, data)` would let one tab's PUT response overwrite
      // the fields the OTHER tab's still-in-flight PUT is saving. So patch ONLY
      // the resolved fields whose source block was present in THIS request's
      // body — the owner-assignment block iff `body.owner_assignment` was sent,
      // the general block iff a general field was sent — leaving the other tab's
      // optimistic/in-flight cache untouched.
      async onQueryStarted(
        { crmType, organizationId, body },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            crmApi.util.updateQueryData(
              'getCrmSettings',
              { crmType, organizationId },
              (draft) => {
                // Owner tab sent its block → patch only the resolved owner field.
                if (body.owner_assignment !== undefined) {
                  draft.owner_assignment = data.owner_assignment;
                }
                // General tab sent any of its fields → patch only the resolved
                // general fields. `create_contacts.enabled` is always part of the
                // General save (see the query body), so its presence marks a
                // General-tab PUT.
                const isGeneralPut =
                  body.create_contacts !== undefined ||
                  body.match_keys !== undefined ||
                  body.safety !== undefined ||
                  body.write_preset !== undefined;
                if (isGeneralPut) {
                  draft.create_contacts = data.create_contacts;
                  draft.create_accounts_independently =
                    data.create_accounts_independently;
                  draft.create_accounts_trigger = data.create_accounts_trigger;
                  draft.account_write_mode = data.account_write_mode;
                  draft.bypass_duplicate_rules = data.bypass_duplicate_rules;
                  draft.in_crm_enrichment_enabled =
                    data.in_crm_enrichment_enabled;
                  if (body.match_keys !== undefined) {
                    draft.match_keys = data.match_keys;
                  }
                  if (body.safety !== undefined) {
                    draft.safety = data.safety;
                  }
                  // Patch the resolved preset only when this PUT sent it, so a
                  // preset-only save doesn't reset the other General fields from a
                  // partial response and an in-flight create_contacts save doesn't
                  // clobber it.
                  if (body.write_preset !== undefined) {
                    draft.write_preset = data.write_preset;
                  }
                }
                // Response-only computed/attribution fields are safe to refresh
                // on either save — neither tab owns them, and the BE recomputes
                // them on every PUT.
                draft.crm_sync_v2_enabled = data.crm_sync_v2_enabled;
                draft.last_edited_by = data.last_edited_by;
              }
            )
          );
        } catch {
          // Mutation failed — nothing to patch; the autosave hook toasts.
        }
      },
      invalidatesTags: (_result, _err, { crmType, organizationId }) => [
        {
          type: 'CrmSettings' as const,
          id: `${organizationId}:${crmType}`,
        },
      ],
    }),

    // Attio's workspace members come from the same route; its settings
    // route stays the pair. `pool` (a play's Salesforce or HubSpot owner
    // pickers) asks for only the users a play's run may pick: no connected
    // Salesforce user, no fallback owner, no inactive user.
    getCrmUsers: builder.query<
      CrmUsersResponse,
      {
        crmType: CrmUserRouteCrm;
        organizationId: string;
        workspaceId?: string;
        pool?: boolean;
      }
    >({
      query: ({ crmType, organizationId, pool }) => ({
        url: `/crm/management/${crmType}/users`,
        ...(pool ? { params: { pool: true } } : {}),
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (_result, _err, { crmType, organizationId }) => [
        {
          type: 'CrmUsers' as const,
          id: `${organizationId}:${crmType}`,
        },
        ...(crmType === 'attio'
          ? [{ type: 'AttioMetadata' as const, id: organizationId }]
          : []),
      ],
      keepUnusedDataFor: 60 * 60 * 24,
    }),

    // CRM status picklist options (CP-4b) — real picklist values per CRM,
    // grouped by field. Powers the account-status MULTI-select (CP-7). Cached
    // long (picklists rarely change) and tagged per crmType so Salesforce and
    // HubSpot keep distinct cache entries.
    getCrmStatusOptions: builder.query<
      CrmStatusOptionsResponse,
      { crmType: 'salesforce' | 'hubspot'; organizationId: string }
    >({
      query: ({ crmType, organizationId }) => ({
        url: `/crm/management/${crmType}/status-options`,
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (_result, _err, { crmType, organizationId }) => [
        {
          type: 'CrmStatusOptions' as const,
          id: `${organizationId}:${crmType}`,
        },
      ],
      keepUnusedDataFor: 60 * 60 * 24,
    }),

    getCrmUserMappings: builder.query<
      CrmUserMappingsResponse,
      { crmType: CrmUserRouteCrm; organizationId: string }
    >({
      query: ({ crmType, organizationId }) => ({
        url: `/crm/management/user-mappings?crm_type=${crmType}`,
        headers: expectedOrganizationHeaders(organizationId),
      }),
      // BE returns { crm_type, rows, fallback }; FE wants { mappings }.
      // Fold the fallback (cleanlist_user_id === null) row in as a mapping.
      transformResponse: (raw: {
        rows?: CrmUserMappingsResponse['mappings'];
        fallback?: CrmUserMappingsResponse['mappings'][number] | null;
      }): CrmUserMappingsResponse => ({
        mappings: [
          ...(raw?.rows ?? []),
          ...(raw?.fallback ? [raw.fallback] : []),
        ],
      }),
      providesTags: (_result, _err, { crmType, organizationId }) => [
        {
          type: 'CrmUserMappings' as const,
          id: `${organizationId}:${crmType}`,
        },
      ],
    }),

    putCrmUserMappings: builder.mutation<
      CrmUserMappingsResponse,
      PutCrmUserMappingsRequest & { organizationId: string }
    >({
      query: ({ organizationId, ...body }) => ({
        url: '/crm/management/user-mappings',
        method: 'PUT',
        headers: expectedOrganizationHeaders(organizationId),
        body,
        keepalive: true,
      }),
      invalidatesTags: (_result, _err, { crm_type, organizationId }) => [
        {
          type: 'CrmUserMappings' as const,
          id: `${organizationId}:${crm_type}`,
        },
      ],
    }),

    getCrmSyncLog: builder.query<SyncLogResponse, GetSyncLogParams>({
      query: ({
        organizationId,
        crm_type,
        from,
        to,
        category,
        state,
        surface,
        sync_profile_id,
        page,
        page_size,
      }) => {
        const params = new URLSearchParams();
        params.set('crm_type', crm_type);
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        if (category) params.set('category', category);
        if (state) {
          params.set('state', Array.isArray(state) ? state.join(',') : state);
        }
        if (surface) params.set('surface', surface);
        if (sync_profile_id) params.set('sync_profile_id', sync_profile_id);
        params.set('page', String(page ?? 1));
        params.set('page_size', String(page_size ?? 100));
        return {
          url: `/crm/management/sync-log?${params.toString()}`,
          headers: expectedOrganizationHeaders(organizationId),
        };
      },
      // BE: flat { entries, total, page, page_size } with string details_chips.
      // FE: { entries, pagination } with { label, value } chips.
      transformResponse: (raw: {
        entries?: Array<Record<string, unknown>>;
        total?: number;
        page?: number;
        page_size?: number;
      }): SyncLogResponse => ({
        entries: (raw?.entries ?? []).map((e) => ({
          ...(e as unknown as SyncLogResponse['entries'][number]),
          details_chips: ((e.details_chips as unknown[]) ?? []).map(
            (
              c
            ): SyncLogResponse['entries'][number]['details_chips'][number] => {
              if (typeof c === 'string') {
                const i = c.indexOf(':');
                return i >= 0
                  ? {
                      label: c.slice(0, i).trim(),
                      value: c.slice(i + 1).trim(),
                    }
                  : { label: c, value: '' };
              }
              return c as { label: string; value: string };
            }
          ),
        })),
        pagination: {
          page: raw?.page ?? 1,
          page_size: raw?.page_size ?? 100,
          total: raw?.total ?? 0,
        },
      }),
      providesTags: (_result, _err, { organizationId, crm_type }) => [
        {
          type: 'CrmSyncLog' as const,
          id: `${organizationId}:${crm_type}`,
        },
      ],
      keepUnusedDataFor: 30,
    }),

    // Re-run the push for a single failed/aborted sync-log entry.
    retrySyncLogEntry: builder.mutation<
      {
        success: boolean;
        message: string;
        crm_record_id?: string | null;
        error?: string | null;
      },
      {
        logId: string;
        organizationId: string;
        crmType: CrmAdminPanelCrm;
      }
    >({
      query: ({ logId, organizationId }) => ({
        url: `/crm/management/sync-log/${logId}/retry`,
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      invalidatesTags: (_result, _err, { organizationId, crmType }) => [
        {
          type: 'CrmSyncLog' as const,
          id: `${organizationId}:${crmType}`,
        },
      ],
    }),

    // -----------------------------------------------------------------------
    // Sync profiles (CLE-560): the CRM settings form saved under a name. Callers
    // skip the list query until the plan gate reads on (see
    // use-sync-profiles-enabled.hook.ts).
    // -----------------------------------------------------------------------

    // Any org member may list; create, update, archive and release are admin
    // only.
    getSyncProfiles: builder.query<
      SyncProfileListResponse,
      { crmType: CrmAdminPanelCrm; organizationId: string }
    >({
      query: ({ crmType, organizationId }) => ({
        url: `/crm/sync-profiles?crm_type=${crmType}`,
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (_result, _err, { crmType, organizationId }) => [
        {
          type: 'SyncProfiles' as const,
          id: `${organizationId}:${crmType}`,
        },
      ],
    }),

    createSyncProfile: builder.mutation<
      SyncProfile,
      { organizationId: string; body: CreateSyncProfileRequest }
    >({
      query: ({ organizationId, body }) => ({
        url: '/crm/sync-profiles',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
        body,
      }),
      invalidatesTags: (_result, _err, { organizationId, body }) => [
        {
          type: 'SyncProfiles' as const,
          id: `${organizationId}:${body.crm_type}`,
        },
      ],
    }),

    updateSyncProfile: builder.mutation<
      SyncProfile,
      {
        id: string;
        crmType: CrmAdminPanelCrm;
        organizationId: string;
        body: UpdateSyncProfileRequest;
      }
    >({
      query: ({ id, organizationId, body }) => ({
        url: `/crm/sync-profiles/${id}`,
        method: 'PATCH',
        headers: expectedOrganizationHeaders(organizationId),
        body,
      }),
      // Turning holds off releases them, and a rename renames "Held by": the
      // rows that show it re-read, as after a release.
      async onQueryStarted({ body }, { dispatch, queryFulfilled }) {
        if (body.hold_policy === undefined && body.name === undefined) return;
        try {
          await queryFulfilled;
          dispatch(leadListsApi.util.invalidateTags(['LeadListLeads']));
        } catch {
          // A failed save leaves the stored holds and their cached rows intact.
        }
      },
      invalidatesTags: (_result, _err, { organizationId, crmType, body }) => [
        {
          type: 'SyncProfiles' as const,
          id: `${organizationId}:${crmType}`,
        },
        ...(body.hold_policy !== undefined || body.name !== undefined
          ? (['CrmSyncStatus'] as const)
          : []),
      ],
    }),

    // 204. A profile a play still uses answers 409 sync_profile_in_use.
    archiveSyncProfile: builder.mutation<
      void,
      { id: string; crmType: CrmAdminPanelCrm; organizationId: string }
    >({
      query: ({ id, organizationId }) => ({
        url: `/crm/sync-profiles/${id}/archive`,
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      // Archiving releases the profile's holds: the rows that show them re-read.
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(leadListsApi.util.invalidateTags(['LeadListLeads']));
        } catch {
          // A refused archive (a play still uses it) changes nothing.
        }
      },
      invalidatesTags: (_result, _err, { organizationId, crmType }) => [
        {
          type: 'SyncProfiles' as const,
          id: `${organizationId}:${crmType}`,
        },
        'CrmSyncStatus',
      ],
    }),

    releaseSyncProfileHolds: builder.mutation<
      { released: number },
      { id: string; crmType: CrmAdminPanelCrm; organizationId: string }
    >({
      query: ({ id, organizationId }) => ({
        url: `/crm/sync-profiles/${id}/release-holds`,
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Rows read held_by from lead-list payloads, including cached pages.
          dispatch(leadListsApi.util.invalidateTags(['LeadListLeads']));
        } catch {
          // A failed release leaves the stored hold and its cached status intact.
        }
      },
      invalidatesTags: (_result, _err, { organizationId, crmType }) => [
        {
          type: 'SyncProfiles' as const,
          id: `${organizationId}:${crmType}`,
        },
        'CrmSyncStatus',
      ],
    }),

    // Releases one person from whichever profile holds them. Refreshes every
    // mounted sync status so the person's popover drops its Held by row.
    releaseSyncProfilePersonHold: builder.mutation<
      { released: 0 | 1 },
      { leadId: string; organizationId: string }
    >({
      query: ({ leadId, organizationId }) => ({
        url: '/crm/sync-profiles/holds/release-person',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
        body: { lead_id: leadId },
      }),
      invalidatesTags: ['SyncProfiles', 'CrmSyncStatus'],
    }),

    // -----------------------------------------------------------------------
    // CRM API usage / daily cap (FE-F)
    // -----------------------------------------------------------------------

    // Today's org-wide CRM API call usage against the editable daily cap, plus
    // display-only remote limits (sf_daily_limit / hubspot_daily_usage). The
    // panel computes the percentage client-side (used_today / cap); there is no
    // cap_pct on the wire.
    getCrmUsage: builder.query<CrmUsageResponse, { organizationId: string }>({
      query: ({ organizationId }) => ({
        url: '/crm/status/usage',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      providesTags: (_result, _err, { organizationId }) => [
        { type: 'CrmUsage' as const, id: organizationId },
      ],
    }),

    // Update (or clear) the org's daily cap override. cap=null resets to the
    // default. Returns the refreshed usage so the meter reflects the new cap;
    // invalidating CrmUsage also refetches any other mounted usage view.
    updateCrmUsageCap: builder.mutation<
      CrmUsageResponse,
      { cap: number | null; organizationId: string }
    >({
      query: ({ cap, organizationId }) => ({
        url: '/crm/status/usage/cap',
        method: 'PUT',
        headers: expectedOrganizationHeaders(organizationId),
        body: { cap },
      }),
      // Patch the usage cache from the PUT's returned UsageResponse so the
      // meter + input reflect the new cap IMMEDIATELY. The tag-invalidation
      // refetch lands a tick later; the panel was re-rendering from the stale
      // cache in between, so the cap looked unchanged until a manual reload
      // (B2). The invalidate still runs to refresh any other mounted view.
      async onQueryStarted({ organizationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            crmApi.util.updateQueryData(
              'getCrmUsage',
              { organizationId },
              (draft) => {
                Object.assign(draft, data);
              }
            )
          );
        } catch {
          // Mutation failed — nothing to patch; the panel's catch toasts.
        }
      },
      invalidatesTags: (_result, _err, { organizationId }) => [
        { type: 'CrmUsage' as const, id: organizationId },
      ],
    }),
  }),
});

export const {
  useGetCrmOptionsQuery,
  useLazyGetCrmOptionsQuery,
  useGetCrmConnectionsQuery,
  useRemoveCrmConnectionMutation,
  useMigrateLeadsToCrmMutation,
  useBackfillListToCrmMutation,
  useGetCrmSyncStatusQuery,
  useGetCrmCompanySyncStatusQuery,
  // [FE-D] rich status popover
  useGetCrmStatusQuery,
  // [perf] forced single-lead status refresh (per-row Retry)
  useRefreshCrmStatusForLeadMutation,
  // [CP-11] rich status popover by identifier (search + directory rows)
  useGetCrmStatusByIdentifierQuery,
  // [perf] forced single-identifier status refresh (per-row Retry, no lead id)
  useRefreshCrmStatusByIdentifierMutation,
  // CRM-based exclusion set (FE-E People-search exclusion filters).
  useGetExclusionSetMutation,
  useGetMartechListsQuery,
  useGetMartechMembersQuery,
  useImportCrmListMutation,
  useToggleCrmPollMutation,
  useTriggerCrmPollNowMutation,
  useGetHubSpotPropertiesQuery,
  useRefreshHubSpotPropertiesMutation,
  useGetFieldMappingTemplatesQuery,
  useLazyGetFieldMappingTemplatesQuery,
  useUpsertDefaultFieldMappingMutation,
  useLookupHubSpotContactsMutation,
  useGetSalesforcePropertiesQuery,
  useGetSalesforceListsQuery,
  useGetSalesforceListViewsQuery,
  useGetSalesforceReportsQuery,
  useGetSalesforceListMembersQuery,
  useLookupSalesforceContactsMutation,
  useConnectLemlistMutation,
  useGetLemlistCampaignsQuery,
  useGetLemlistPropertiesQuery,
  useGetAttioPropertiesQuery,
  useGetAttioAttributesQuery,
  useGetAttioListsQuery,
  useGetPipedrivePropertiesQuery,
  // CRM Admin Panel V1 (CLE-310)
  useGetCrmSettingsQuery,
  useLazyGetCrmSettingsQuery,
  usePutCrmSettingsMutation,
  useGetCrmUsersQuery,
  useGetCrmStatusOptionsQuery,
  useGetCrmUserMappingsQuery,
  usePutCrmUserMappingsMutation,
  useGetCrmSyncLogQuery,
  useRetrySyncLogEntryMutation,
  // CRM API usage / daily cap (FE-F)
  useGetCrmUsageQuery,
  useUpdateCrmUsageCapMutation,
  // Sync profiles (CLE-560)
  useGetSyncProfilesQuery,
  useCreateSyncProfileMutation,
  useUpdateSyncProfileMutation,
  useArchiveSyncProfileMutation,
  useReleaseSyncProfileHoldsMutation,
  useReleaseSyncProfilePersonHoldMutation,
} = crmApi;
